/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  UserCheck,
  ShieldCheck,
  ShieldAlert,
  QrCode,
  DoorOpen,
  Clock,
  Phone,
  Plus,
  CheckCircle2,
  XCircle,
  Home,
  Loader2,
  RefreshCw,
  HandHelping,
  CalendarCheck,
} from "lucide-react";
import { gateApi } from "./gateApi";
import { dailyHelpApi, helpKindLabel } from "../dailyhelp/api";
import { amenitiesApi } from "../amenities/api";
import { useAuth } from "../auth/AuthContext";
import { Avatar, toast } from "../ui";
import "./VisitorApprovalPage.css";

const POLL_MS = 12000;

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return "";
  }
}

function fmtWindow(from, to) {
  if (!from || !to) return "";
  try {
    const f = new Date(from);
    const t = new Date(to);
    const sameDay = f.toDateString() === t.toDateString();
    const d = (x) =>
      x.toLocaleDateString([], { day: "numeric", month: "short" });
    return sameDay
      ? `${d(f)}, ${fmtTime(from)}–${fmtTime(to)}`
      : `${d(f)} ${fmtTime(from)} – ${d(t)} ${fmtTime(to)}`;
  } catch (_) {
    return "";
  }
}

export default function VisitorApprovalPage() {
  const { user } = useAuth();
  // Clubhouse booking verify is backend-gated to CLUBHOUSE_SECURITY +
  // SECURITY_INCHARGE. Of the roles that reach /gate (admin/FM/incharge), only
  // the incharge can verify — so only show the tab to them (avoids a dead 403
  // tab for admin/FM).
  const canVerifyClubhouse =
    (user?.active_role || (user?.is_admin ? "ADMIN" : "RESIDENT")) ===
    "SECURITY_INCHARGE";
  const [expected, setExpected] = useState([]);
  const [queue, setQueue] = useState([]);
  const [recent, setRecent] = useState([]);
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("visitors");

  const loadLists = useCallback(async (signal) => {
    try {
      const [exp, q, rec] = await Promise.all([
        gateApi.expected({ signal }),
        gateApi.list({ status: "PENDING,APPROVED" }, { signal }),
        gateApi.list({ status: "INSIDE,EXITED" }, { signal }),
      ]);
      setExpected(exp || []);
      setQueue(q || []);
      setRecent((rec || []).slice(0, 12));
    } catch (e) {
      if (e.name !== "AbortError") {
        // Soft-fail the polling; the action cards still work.
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    loadLists(ctrl.signal);
    gateApi
      .villas({ signal: ctrl.signal })
      .then((v) => setVillas(v || []))
      .catch(() => {});
    const id = setInterval(() => loadLists(), POLL_MS);
    return () => {
      ctrl.abort();
      clearInterval(id);
    };
  }, [loadLists]);

  const refresh = useCallback(() => loadLists(), [loadLists]);

  return (
    <div className="vga-shell">
      <div className="vga-head">
        <div>
          <h1>
            <UserCheck size={22} /> Visitor Approval
          </h1>
          <p className="vga-sub">
            Admit pre-approved guests by their pass code, verify a resident's
            identity, and log walk-ups for the villa to approve.
          </p>
        </div>
        <button className="vga-refresh" onClick={refresh} title="Refresh">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      <div className="vga-tabs" role="tablist">
        {[
          ["visitors", "Visitors", UserCheck],
          ["help", "Daily Help", HandHelping],
          ...(canVerifyClubhouse
            ? [["clubhouse", "Clubhouse", CalendarCheck]]
            : []),
          ["verify", "Verify ID", ShieldCheck],
        ].map(([key, label, Icon]) => (
          <button
            key={key}
            className={`vga-tab ${tab === key ? "active" : ""}`}
            onClick={() => setTab(key)}
          >
            <Icon size={15} /> {label}
            {key === "visitors" && queue.length > 0 && (
              <span className="vga-tab-badge">{queue.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "visitors" && (
        <>
          <div className="vga-actions vga-actions--single">
            <AdmitByCode onAdmitted={refresh} />
          </div>
          <div className="vga-grid">
            <ExpectedList items={expected} loading={loading} />
            <WalkupPanel villas={villas} queue={queue} onChanged={refresh} />
            <RecentList items={recent} onChanged={refresh} />
          </div>
        </>
      )}

      {tab === "help" && (
        <div className="vga-actions vga-actions--single">
          <DailyHelpGate />
        </div>
      )}

      {tab === "clubhouse" && (
        <div className="vga-actions vga-actions--single">
          <ClubhouseBookingVerify />
        </div>
      )}

      {tab === "verify" && (
        <div className="vga-actions vga-actions--single">
          <VerifyResident />
        </div>
      )}
    </div>
  );
}

// --- Admit by OTP / QR -----------------------------------------------------

function AdmitByCode({ onAdmitted }) {
  const [otp, setOtp] = useState("");
  const [purpose, setPurpose] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {ok, visitor} | {ok:false, msg}
  const inputRef = useRef(null);

  const submit = async (e) => {
    e?.preventDefault();
    const code = otp.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    setResult(null);
    try {
      const v = await gateApi.verifyEntry({
        otp: code,
        purpose: purpose.trim() || undefined,
        vehicle_no: vehicle.trim() || undefined,
      });
      setResult({ ok: true, visitor: v });
      setOtp("");
      setPurpose("");
      setVehicle("");
      onAdmitted?.();
      inputRef.current?.focus();
    } catch (err) {
      setResult({ ok: false, msg: err.message || "Could not admit" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="vga-card vga-card--primary">
      <div className="vga-card-head">
        <span className="vga-ic vga-ic--blue">
          <QrCode size={18} />
        </span>
        <div>
          <h2>Admit by pass code</h2>
          <p>Type the 6-character code the visitor shows, or scan their QR.</p>
        </div>
      </div>

      <form onSubmit={submit} className="vga-codeform">
        <input
          ref={inputRef}
          className="vga-otp"
          value={otp}
          onChange={(e) => setOtp(e.target.value.toUpperCase())}
          placeholder="A1B2C3"
          maxLength={48}
          autoFocus
          spellCheck={false}
          autoComplete="off"
        />
        <div className="vga-codeextra">
          <input
            className="vga-mini"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Purpose (optional)"
          />
          <input
            className="vga-mini"
            value={vehicle}
            onChange={(e) => setVehicle(e.target.value)}
            placeholder="Vehicle no. (optional)"
          />
        </div>
        <button className="vga-btn vga-btn--admit" disabled={busy || !otp.trim()}>
          {busy ? <Loader2 size={16} className="vga-spin" /> : <DoorOpen size={16} />}
          Admit visitor
        </button>
      </form>

      {result && result.ok && (
        <div className="vga-banner vga-banner--ok">
          <CheckCircle2 size={18} />
          <div>
            <strong>{result.visitor.name}</strong> admitted to{" "}
            <strong>{result.visitor.villa_number || "—"}</strong>.
          </div>
        </div>
      )}
      {result && !result.ok && (
        <div className="vga-banner vga-banner--err">
          <XCircle size={18} />
          <div>{result.msg}</div>
        </div>
      )}
    </section>
  );
}

// --- Verify a resident's identity QR --------------------------------------

function VerifyResident() {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // identity payload | {error}

  const submit = async (e) => {
    e?.preventDefault();
    const raw = token.trim();
    if (!raw) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await gateApi.verifyIdentity({ token: raw, qr_code: raw });
      setResult(r);
      setToken("");
    } catch (err) {
      setResult({ error: err.message || "Not a valid resident code" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="vga-card">
      <div className="vga-card-head">
        <span className="vga-ic vga-ic--green">
          <ShieldCheck size={18} />
        </span>
        <div>
          <h2>Verify a resident</h2>
          <p>Scan or paste a resident's personal QR to confirm they belong here.</p>
        </div>
      </div>

      <form onSubmit={submit} className="vga-codeform">
        <input
          className="vga-mini vga-mini--wide"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Paste resident QR token"
          spellCheck={false}
          autoComplete="off"
        />
        <button className="vga-btn vga-btn--verify" disabled={busy || !token.trim()}>
          {busy ? <Loader2 size={16} className="vga-spin" /> : <ShieldCheck size={16} />}
          Verify
        </button>
      </form>

      {result && result.error && (
        <div className="vga-banner vga-banner--err">
          <XCircle size={18} />
          <div>{result.error}</div>
        </div>
      )}
      {result && !result.error && (
        <div
          className={`vga-idcard ${
            result.verified ? "vga-idcard--ok" : "vga-idcard--no"
          }`}
        >
          <Avatar imageUrl={result.avatar_url} name={result.name} size={48} />
          <div className="vga-idmeta">
            <strong>{result.name}</strong>
            <div className="vga-idvillas">
              {(result.villas || []).map((v) => (
                <span key={v.villa_number} className="vga-idvilla">
                  <Home size={12} /> {v.villa_number} · {v.role_label}
                </span>
              ))}
              {(result.villas || []).length === 0 && (
                <span className="vga-muted">No active villa membership</span>
              )}
            </div>
          </div>
          <span
            className={`vga-idbadge ${
              result.verified ? "vga-idbadge--ok" : "vga-idbadge--no"
            }`}
          >
            {result.verified ? (
              <>
                <ShieldCheck size={14} /> Verified member
              </>
            ) : (
              <>
                <ShieldAlert size={14} /> Not a member
              </>
            )}
          </span>
        </div>
      )}
    </section>
  );
}

// --- Daily Help: mark a helper in/out by code -----------------------------

function DailyHelpGate() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    const sc = code.trim().toUpperCase();
    if (!sc) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await dailyHelpApi.gateScan({ short_code: sc });
      setResult({ ok: true, ...r });
      setCode("");
    } catch (err) {
      setResult({ ok: false, msg: err.message || "Not a valid helper code" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="vga-card">
      <div className="vga-card-head">
        <span className="vga-ic vga-ic--amber">
          <HandHelping size={18} />
        </span>
        <div>
          <h2>Daily Help</h2>
          <p>Enter a helper's code to mark them in or out.</p>
        </div>
      </div>
      <form onSubmit={submit} className="vga-codeform">
        <input
          className="vga-mini vga-mini--wide"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Helper code (e.g. 7K2QP4)"
          spellCheck={false}
          autoComplete="off"
        />
        <button className="vga-btn vga-btn--admit" disabled={busy || !code.trim()}>
          {busy ? <Loader2 size={16} className="vga-spin" /> : <DoorOpen size={16} />}
          Mark in / out
        </button>
      </form>
      {result && result.ok && (
        <div className="vga-banner vga-banner--ok">
          <CheckCircle2 size={18} />
          <div>
            <strong>{result.staff.name}</strong> ({helpKindLabel(result.staff.kind)})
            marked <strong>{result.action === "in" ? "IN" : "OUT"}</strong> ·{" "}
            {result.staff.villa_number}
          </div>
        </div>
      )}
      {result && !result.ok && (
        <div className="vga-banner vga-banner--err">
          <XCircle size={18} />
          <div>{result.msg}</div>
        </div>
      )}
    </section>
  );
}

// --- Clubhouse: verify an amenity booking by short code -------------------

function ClubhouseBookingVerify() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {ok, booking} | {ok:false, msg}

  const submit = async (e) => {
    e?.preventDefault();
    const sc = code.trim().toUpperCase();
    if (!sc) return;
    setBusy(true);
    setResult(null);
    try {
      // Web has no camera fallback here — security keys the 6-char short code
      // off the resident's booking pass. The server re-checks slot time and
      // status (already-used / cancelled / expired / wrong-time) on verify.
      const r = await amenitiesApi.verifyBooking({ short_code: sc });
      setResult({ ok: true, booking: r.booking });
      setCode("");
    } catch (err) {
      setResult({ ok: false, msg: err.message || "Could not verify this booking" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="vga-card">
      <div className="vga-card-head">
        <span className="vga-ic vga-ic--green">
          <CalendarCheck size={18} />
        </span>
        <div>
          <h2>Clubhouse booking</h2>
          <p>Enter the 6-character booking code the resident shows to check them in.</p>
        </div>
      </div>
      <form onSubmit={submit} className="vga-codeform">
        <input
          className="vga-mini vga-mini--wide"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Booking code (e.g. 7K2QP4)"
          maxLength={6}
          spellCheck={false}
          autoComplete="off"
        />
        <button className="vga-btn vga-btn--verify" disabled={busy || !code.trim()}>
          {busy ? <Loader2 size={16} className="vga-spin" /> : <CalendarCheck size={16} />}
          Verify booking
        </button>
      </form>

      {result && result.ok && (
        <div className="vga-banner vga-banner--ok">
          <CheckCircle2 size={18} />
          <div>
            <strong>{result.booking.resident_name || "Resident"}</strong>
            {result.booking.villa_number ? ` · ${result.booking.villa_number}` : ""} checked
            in for <strong>{result.booking.amenity_name || "the amenity"}</strong>
            {fmtWindow(result.booking.slot_start, result.booking.slot_end)
              ? ` · ${fmtWindow(result.booking.slot_start, result.booking.slot_end)}`
              : ""}
            .
          </div>
        </div>
      )}
      {result && !result.ok && (
        <div className="vga-banner vga-banner--err">
          <XCircle size={18} />
          <div>{result.msg}</div>
        </div>
      )}
    </section>
  );
}

// --- Expected (live passes) -----------------------------------------------

function ExpectedList({ items, loading }) {
  return (
    <section className="vga-list">
      <header className="vga-list-head">
        <h3>
          <Clock size={15} /> Expected at the gate
        </h3>
        <span className="vga-count">{items.length}</span>
      </header>
      {loading && items.length === 0 ? (
        <div className="vga-empty">Loading…</div>
      ) : items.length === 0 ? (
        <div className="vga-empty">No live passes right now.</div>
      ) : (
        <ul className="vga-rows">
          {items.map((inv) => (
            <li key={inv.id} className="vga-row">
              <div className="vga-row-main">
                <strong>{inv.guest_name}</strong>
                <span className="vga-row-sub">
                  {inv.villa_number || "—"} · {fmtWindow(inv.valid_from, inv.valid_to)}
                </span>
              </div>
              <span className="vga-uses">
                {Math.max(0, (inv.max_uses || 0) - (inv.use_count || 0))} left
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// --- Walk-ups: log + pending queue ----------------------------------------

function WalkupPanel({ villas, queue, onChanged }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [villaId, setVillaId] = useState("");
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);
  const [overrideFor, setOverrideFor] = useState(null);
  const [note, setNote] = useState("");

  const sortedVillas = useMemo(
    () =>
      [...(villas || [])].sort((a, b) => {
        const na = parseInt((a.villa_number || "").replace(/\D/g, ""), 10) || 0;
        const nb = parseInt((b.villa_number || "").replace(/\D/g, ""), 10) || 0;
        return na - nb;
      }),
    [villas]
  );

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !villaId) {
      toast.warning("Name and villa are required");
      return;
    }
    setBusy(true);
    try {
      await gateApi.logWalkup({
        villa_id: villaId,
        name: name.trim(),
        kind: "GUEST",
        phone: phone.trim() || undefined,
        purpose: purpose.trim() || undefined,
      });
      toast.success("Logged — the villa has been notified to approve.");
      setName("");
      setPhone("");
      setPurpose("");
      setVillaId("");
      setOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Could not log the walk-up");
    } finally {
      setBusy(false);
    }
  };

  const admit = async (v) => {
    try {
      await gateApi.checkIn(v.id);
      toast.success(`${v.name} checked in.`);
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Could not check in");
    }
  };

  const doOverride = async (v) => {
    if (!note.trim()) {
      toast.warning("A note is required to override.");
      return;
    }
    try {
      await gateApi.override(v.id, { override_note: note.trim() });
      toast.success(`${v.name} approved via override.`);
      setOverrideFor(null);
      setNote("");
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Could not override");
    }
  };

  return (
    <section className="vga-list">
      <header className="vga-list-head">
        <h3>
          <Phone size={15} /> Walk-ups
        </h3>
        <button className="vga-add" onClick={() => setOpen((o) => !o)}>
          <Plus size={14} /> Log walk-up
        </button>
      </header>

      {open && (
        <form onSubmit={submit} className="vga-walkform">
          <input
            className="vga-mini"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Visitor name"
          />
          <input
            className="vga-mini"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone (optional)"
          />
          <select
            className="vga-mini"
            value={villaId}
            onChange={(e) => setVillaId(e.target.value)}
          >
            <option value="">Select villa…</option>
            {sortedVillas.map((v) => (
              <option key={v.id} value={v.id}>
                {v.villa_number}
              </option>
            ))}
          </select>
          <input
            className="vga-mini"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Purpose (optional)"
          />
          <button className="vga-btn vga-btn--admit" disabled={busy}>
            {busy ? <Loader2 size={15} className="vga-spin" /> : <Plus size={15} />}
            Notify villa
          </button>
        </form>
      )}

      {queue.length === 0 ? (
        <div className="vga-empty">No one waiting.</div>
      ) : (
        <ul className="vga-rows">
          {queue.map((v) => (
            <li key={v.id} className="vga-row">
              <div className="vga-row-main">
                <strong>{v.name}</strong>
                <span className="vga-row-sub">
                  {v.villa_number || "—"}
                  {v.purpose ? ` · ${v.purpose}` : ""} ·{" "}
                  <span className={`vga-tag vga-tag--${(v.status || "").toLowerCase()}`}>
                    {v.status}
                  </span>
                </span>
                {overrideFor === v.id && (
                  <div className="vga-overrow">
                    <input
                      className="vga-mini vga-mini--wide"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Override note (required)"
                      autoFocus
                    />
                    <button
                      className="vga-btn vga-btn--sm"
                      onClick={() => doOverride(v)}
                    >
                      Confirm
                    </button>
                  </div>
                )}
              </div>
              <div className="vga-row-actions">
                {v.status === "APPROVED" ? (
                  <button className="vga-btn vga-btn--sm vga-btn--admit" onClick={() => admit(v)}>
                    <DoorOpen size={14} /> Admit
                  </button>
                ) : (
                  <button
                    className="vga-btn vga-btn--sm vga-btn--ghost"
                    onClick={() => {
                      setOverrideFor(overrideFor === v.id ? null : v.id);
                      setNote("");
                    }}
                  >
                    Override
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// --- Recently admitted -----------------------------------------------------

function RecentList({ items, onChanged }) {
  const [busyId, setBusyId] = useState(null);

  const checkout = async (v) => {
    setBusyId(v.id);
    try {
      await gateApi.checkOut(v.id);
      toast.success(`${v.name} checked out.`);
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Could not check out");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="vga-list">
      <header className="vga-list-head">
        <h3>
          <DoorOpen size={15} /> Recently at the gate
        </h3>
        <span className="vga-count">{items.length}</span>
      </header>
      {items.length === 0 ? (
        <div className="vga-empty">Nothing yet today.</div>
      ) : (
        <ul className="vga-rows">
          {items.map((v) => (
            <li key={v.id} className="vga-row">
              <div className="vga-row-main">
                <strong>{v.name}</strong>
                <span className="vga-row-sub">
                  {v.villa_number || "—"} ·{" "}
                  {v.status === "INSIDE" ? "Inside" : "Left"} ·{" "}
                  {fmtTime(v.entered_at || v.created_at)}
                </span>
              </div>
              <div className="vga-row-actions">
                <span className={`vga-tag vga-tag--${(v.status || "").toLowerCase()}`}>
                  {v.status}
                </span>
                {v.status === "INSIDE" && (
                  <button
                    className="vga-btn vga-btn--sm vga-btn--checkout"
                    disabled={busyId === v.id}
                    onClick={() => checkout(v)}
                  >
                    {busyId === v.id ? <Loader2 size={13} className="vga-spin" /> : <DoorOpen size={13} />}
                    Check out
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
